/**
 * Plan rotation for framing: the app's standard number field (type, ‹ ›
 * arrows, drag to scrub — a slow drag goes below whole degrees, Shift finer
 * still) between two quarter-turn buttons.
 */

import { ArrowUturnLeftIcon, ArrowUturnRightIcon } from '@heroicons/react/24/outline';
import { memo, useState } from 'react';
import { Input } from '../../components/inputs/Input';
import { useT } from '../../translation/src';

/** Wrap to (−180, 180] with hundredths. */
export function normalizeHeading(deg: number): number {
  const wrapped = (((deg % 360) + 540) % 360) - 180;
  return Math.round((wrapped === -180 ? 180 : wrapped) * 100) / 100;
}

interface RotationControlProps {
  value: number;
  onChange: (deg: number) => void;
}

const RotationControl_: React.FC<RotationControlProps> = ({ value, onChange }) => {
  const labels = { left: useT('quarter turn left', { swc: true }), right: useT('quarter turn right', { swc: true }), rotation: useT('rotation', { swc: true }) };
  const button = 'rounded p-1.5 hover:bg-black/10 dark:hover:bg-white/10';
  /** What is being typed (half-typed values like "2." or "-" stay as typed); the plan turns with every complete number. */
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <div className="flex items-center gap-1">
      <button type="button" className={button} onClick={() => onChange(normalizeHeading(value + 90))} title={labels.left} aria-label={labels.left}>
        <ArrowUturnLeftIcon className="h-5 w-5" />
      </button>
      <Input
        type="number"
        value={draft ?? value}
        unit="°"
        aria-label={labels.rotation}
        title={labels.rotation}
        className="h-8 w-28 text-sm"
        onChange={(e) => {
          const text = e.target.value;
          setDraft(text);
          const next = Number(text);
          if (text !== '' && Number.isFinite(next)) onChange(normalizeHeading(next));
        }}
        onBlur={() => setDraft(null)}
      />
      <button type="button" className={button} onClick={() => onChange(normalizeHeading(value - 90))} title={labels.right} aria-label={labels.right}>
        <ArrowUturnRightIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

export const RotationControl = memo(RotationControl_);
